import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useEtatInfos } from "./etatInfos.js";
import { IconeAccueil, IconeChevronDroite, IconeCommandes, IconeCredits, IconePleinEcran } from "./icones.js";
import { indexMenu, raccourciPleinEcran } from "./logiqueInfos.js";
import { Signature } from "./Signature.js";
import { useClavierSurcouche } from "./useClavierSurcouche.js";
import { useInfos, usePleinEcran } from "./useInfos.js";
import * as s from "./infosStyles.js";
import "./infos.css";

/**
 * ============================================================================
 *  MenuContextuel.tsx — Le menu d'informations, sur ordinateur
 * ----------------------------------------------------------------------------
 *  Référence : `sources/design/design_handoff_credits/` (9a). Un menu `role="menu"` de
 *  320 px, ancré SOUS le bouton qui l'a ouvert (52 px sous son bord haut, aligné à sa
 *  droite) — le bouton peut être dans le groupe des langues, dans la bande du guide ou
 *  à côté de « Quitter » : on mesure plutôt que de supposer sa place.
 *
 *  Clavier : ↑ ↓ (et Début / Fin) entre les entrées, Échap ferme, F bascule le plein
 *  écran. Un clic à l'extérieur ferme. Dans tous les cas le focus est rendu au bouton.
 * ==========================================================================*/

const DECALAGE_SOUS_BOUTON = 52;

export function MenuContextuel({ surAccueil }: { surAccueil: () => void }) {
  const infos = useInfos();
  const { t } = infos;
  const origine = useEtatInfos((e) => e.origine);
  const ancre = useEtatInfos((e) => e.ancre);
  const pleinEcran = usePleinEcran();
  const racine = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left?: number; right?: number } | null>(null);

  // Ancrage : mesuré avant la peinture, pour que le menu n'apparaisse jamais à la mauvaise place.
  useLayoutEffect(() => {
    const hote = racine.current?.parentElement;
    if (!hote) return;
    const cadre = hote.getBoundingClientRect();
    if (ancre) {
      // Clic droit : au curseur, ramené dans l'écran (le menu bascule vers la gauche / le haut au bord).
      const menu = racine.current;
      const largeur = menu?.offsetWidth ?? 320;
      const hauteur = menu?.offsetHeight ?? 0;
      const borne = (valeur: number, max: number) => Math.max(8, Math.min(valeur, max - 8));
      setPosition({
        left: borne(ancre.x - cadre.left, cadre.width - largeur),
        top: borne(ancre.y - cadre.top, cadre.height - hauteur),
      });
    } else if (origine) {
      const bouton = origine.getBoundingClientRect();
      setPosition({ top: bouton.top - cadre.top + DECALAGE_SOUS_BOUTON, right: cadre.right - bouton.right });
    }
  }, [origine, ancre]);

  // Le focus entre sur la première entrée dès que le menu est placé (masqué, il ne peut le recevoir)…
  useEffect(() => {
    if (position) racine.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus({ preventScroll: true });
  }, [position]);

  // …et revient au bouton à la fermeture.
  useEffect(
    () => () => {
      if (origine?.isConnected) origine.focus({ preventScroll: true });
    },
    // `origine` ne change pas tant que le menu est monté.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Clic à l'extérieur : le bouton lui-même bascule (son propre `onClick`), il ne compte pas comme extérieur.
  useEffect(() => {
    const surPointeur = (event: PointerEvent) => {
      const cible = event.target as Node | null;
      if (!cible || racine.current?.contains(cible) || origine?.contains(cible)) return;
      infos.fermerMenu();
    };
    document.addEventListener("pointerdown", surPointeur, true);
    return () => document.removeEventListener("pointerdown", surPointeur, true);
  }, [infos, origine]);

  useClavierSurcouche(true, (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      infos.fermerMenu();
      return;
    }
    if (raccourciPleinEcran(event.key, false, false)) {
      event.preventDefault();
      pleinEcran.basculer();
      return;
    }
    const entrees = [...(racine.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const suivante = indexMenu(entrees.indexOf(document.activeElement as HTMLElement), entrees.length, event.key);
    if (suivante === null) return;
    event.preventDefault();
    entrees[suivante]?.focus({ preventScroll: true });
  });

  return (
    <div
      ref={racine}
      role="menu"
      aria-label={t("infoOuvrir")}
      className="inf-menu"
      style={{
        ...s.menu,
        top: position?.top ?? 0,
        ...(position?.left !== undefined ? { left: position.left } : { right: position?.right ?? 0 }),
        transformOrigin: position?.left !== undefined ? "top left" : undefined,
        visibility: position ? "visible" : "hidden",
      }}
    >
      <h2 style={s.titreMenu}>{t("infoBienvenue")}</h2>

      <button type="button" role="menuitem" className="inf-ligne" data-inf-ligne="credits" onClick={infos.ouvrirCredits} style={s.ligne(44, 14)}>
        <IconeCredits />
        {t("infoCredits")}
        <span style={{ marginLeft: "auto", display: "inline-flex" }}>
          <IconeChevronDroite taille={16} />
        </span>
      </button>

      <button type="button" role="menuitem" className="inf-ligne" onClick={infos.rejouerIndice} style={s.ligne(44, 14)}>
        <IconeCommandes />
        {t("infoCommandes")}
      </button>

      {pleinEcran.disponible && (
        <button
          type="button"
          role="menuitem"
          className="inf-ligne"
          onClick={() => {
            pleinEcran.basculer();
            infos.fermerMenu();
          }}
          style={s.ligne(44, 14)}
        >
          <IconePleinEcran sortie={pleinEcran.plein} />
          {pleinEcran.plein ? t("infoQuitterPleinEcran") : t("infoPleinEcran")}
          <kbd aria-hidden style={s.raccourci}>
            F
          </kbd>
        </button>
      )}

      <button type="button" role="menuitem" className="inf-ligne" onClick={() => infos.allerAccueil(surAccueil)} style={s.ligne(44, 14)}>
        <IconeAccueil />
        {t("infoAccueil")}
      </button>

      <Signature t={t} lien surLien={infos.lien} rolePourMenu />
    </div>
  );
}
